import React, { useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useEmblaCarousel from 'embla-carousel-react'

// Components
import ProgressBar from 'react-bootstrap/ProgressBar'
import CardLoading from '../../components/component-items/loading-popover'
import { useFetch } from '../../components/component-items/helpers'
import { CarouselItemStyled, AsnStyled } from './styles'

// Enums
import { ShipNoticeStatus } from '../../enums'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight, faBoxOpen } from '@fortawesome/pro-regular-svg-icons'

// Types
import { CarouselItemProps } from './types'

const CarouselItem = ({
  title,
  desc,
  arriving,
  inOrders,
  dateArrival,
  previewUrl,
  detailUrl,
  progress,
}: CarouselItemProps) => {
  return (
    <CarouselItemStyled>
      <div className="body">
        <div className="widget__product">
          <div className="thumb">
            <Link to={detailUrl}>
              <i>
                <FontAwesomeIcon icon={faBoxOpen} />
              </i>
            </Link>
          </div>
          <div className="widget__content">
            <Link to={detailUrl}>
              <h3>{title}</h3>
            </Link>
            <div className="desc">{desc}</div>
          </div>
        </div>
        <div className="widget__data">
          <div className="widget-info">
            <div className="info">{arriving}</div>
            <div className="desc">Arriving</div>
          </div>
          <div className="widget-info">
            <div className="title">{inOrders}</div>
            <div className="desc">In Orders</div>
          </div>
        </div>
      </div>
      <div className="footer">
        {progress ? (
          <div className="progress_bar">
            <div className="label">Progress</div>
            <div className="value">{progress}%</div>
            <ProgressBar striped animated variant="success" now={progress} key={1} />
          </div>
        ) : (
          <>
            <div className="footer__info">
              <div className="label">{dateArrival}</div>
              <div className="desc">Date of Expected Arrival</div>
            </div>
            <div className="footer__toolbar">
              <Link to={previewUrl}>Preview</Link>
              <Link to={detailUrl}>Details</Link>
            </div>
          </>
        )}
      </div>
    </CarouselItemStyled>
  )
}

export const AsnCard = ({ url }: { url: string }) => {
  const resp: any = useFetch(url, {})
  const asns = resp.loaded && resp.response ? resp.response.asns : []
  const total = resp.loaded && resp.response ? resp.response.total : 0

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', containScroll: 'trimSnaps' })

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  // Re-initialize embla when data loads to recalculate slide dimensions
  useEffect(() => {
    if (emblaApi && resp.loaded) {
      emblaApi.reInit()
    }
  }, [resp.loaded, emblaApi])

  return (
    <AsnStyled>
      <div className="head">
        <div className="label">ASNs - {total}</div>
        <div className="nav">
          <button onClick={scrollPrev}>
            <i>
              <FontAwesomeIcon icon={faChevronLeft} />
            </i>
          </button>
          <button onClick={scrollNext}>
            <i>
              <FontAwesomeIcon icon={faChevronRight} />
            </i>
          </button>
        </div>
      </div>

      {resp.loaded && asns.length > 0 && (
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {asns.map((a: any) => (
              <div className="embla__slide" key={a.id}>
                <CarouselItem
                  title={a.company}
                  desc={'Sent By: ' + a.sender}
                  arriving={a.sent}
                  inOrders={a.inOrder}
                  dateArrival={a.deliveryDate}
                  previewUrl={'/asn/'}
                  detailUrl={`/asn/${a.id}/`}
                  progress={a.status === ShipNoticeStatus.Arrived ? a.progress : null}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {resp.loaded && asns.length === 0 && (
        <div className="no-issues">
          <div role="img" aria-label="celebrate">
            📦
          </div>
          No Incoming ASNs!
        </div>
      )}

      {!resp.loaded && <CardLoading text={resp.placeholder} error={resp.error} />}
    </AsnStyled>
  )
}

export default AsnCard
